import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { sceneKeys } from '../scenes/queries'
import { toSceneSheet } from '../scenes/types'
import { toCaseSheet, type CaseCandidate, type CaseSheet, type CaseSummary } from './types'

type Schema = components['schemas']

export const caseKeys = {
  all: ['cases'] as const,
  lists: () => [...caseKeys.all, 'list'] as const,
  list: (campaignId: number, page: number, pageSize: number) =>
    [...caseKeys.lists(), campaignId, page, pageSize] as const,
  sheets: () => [...caseKeys.all, 'sheet'] as const,
  sheet: (caseId: number) => [...caseKeys.sheets(), caseId] as const,
  candidates: (caseId: number) => [...caseKeys.all, caseId, 'available-characters'] as const,
}

export interface CaseListPage {
  items: CaseSummary[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

/** A campaign's cases, newest first. Any member may read them. */
export function useCaseList(
  campaignId: number,
  page = 1,
  pageSize = 50,
): UseQueryResult<CaseListPage> {
  return useQuery({
    queryKey: caseKeys.list(campaignId, page, pageSize),
    enabled: Number.isFinite(campaignId),
    queryFn: async () => {
      const result = await api.get<Schema['CaseSummaryDtoPagedResult']>(
        `/campaigns/${campaignId}/cases`,
        { query: { page, pageSize } },
      )

      return {
        items: result.items ?? [],
        page: result.page ?? page,
        pageSize: result.pageSize ?? pageSize,
        totalCount: result.totalCount ?? 0,
        totalPages: result.totalPages ?? 0,
      }
    },
  })
}

/** One case: core, its major characters and its scenes in play order. */
export function useCaseSheet(caseId: number): UseQueryResult<CaseSheet> {
  return useQuery({
    queryKey: caseKeys.sheet(caseId),
    enabled: Number.isFinite(caseId),
    queryFn: async () => toCaseSheet(await api.get<Schema['CaseSheetDto']>(`/cases/${caseId}`)),
  })
}

/**
 * Campaign-roster characters not yet on the case.
 *
 * Server-filtered — the endpoint excludes the ones already attached, so the client never
 * subtracts one list from another and never offers a character the API would refuse.
 */
export function useCaseCandidates(caseId: number, enabled = true): UseQueryResult<CaseCandidate[]> {
  return useQuery({
    queryKey: caseKeys.candidates(caseId),
    enabled: enabled && Number.isFinite(caseId),
    queryFn: () => api.get<CaseCandidate[]>(`/cases/${caseId}/available-characters`),
  })
}

export function useCreateCase(campaignId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { name: string; description?: string | undefined }) =>
      toCaseSheet(
        await api.post<Schema['CaseSheetDto']>(`/campaigns/${campaignId}/cases`, {
          name: input.name,
          description: input.description ?? null,
        }),
      ),

    onSuccess: (sheet) => {
      // Creation returns a whole sheet rather than an envelope patch, and it changes a
      // list this cache holds — the one case where invalidating is right.
      void queryClient.invalidateQueries({ queryKey: caseKeys.lists() })
      queryClient.setQueryData(caseKeys.sheet(sheet.caseId), sheet)
    },
  })
}

/**
 * Creates a scene in a case.
 *
 * The one scene-shaped mutation that is not an envelope patch: it answers `201` with the
 * **new scene's own sheet**, because what a client does next is navigate to it. Nothing
 * comes back describing the case, so the case entry is invalidated rather than patched —
 * its `scenes` list and its `core.sceneCount` both moved and no envelope carries them.
 *
 * The returned sheet seeds the scene's own cache entry, so arriving at the tracker costs
 * no second read.
 */
export function useCreateScene(caseId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: Schema['CreateSceneRequest']) =>
      toSceneSheet(await api.post<Schema['SceneSheetDto']>(`/cases/${caseId}/scenes`, input)),

    onSuccess: (sheet) => {
      queryClient.setQueryData(sceneKeys.sheet(sheet.sceneId), sheet)
      void queryClient.invalidateQueries({ queryKey: caseKeys.sheet(caseId) })
    },
  })
}

/** Deletes a case and its scenes. Answers 204, so there is no sheet left to patch. */
export function useDeleteCase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (caseId: number) => api.delete<void>(`/cases/${caseId}`),

    onSuccess: (_result, caseId) => {
      queryClient.removeQueries({ queryKey: caseKeys.sheet(caseId) })
      void queryClient.invalidateQueries({ queryKey: caseKeys.lists() })
    },
  })
}
